import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff, User, Lock } from 'lucide-react';

const LoginPage = () => {
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    setLoading(true);
    const result = await login(email, password);
    
    if (!result.success) {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <div className="space-y-8 text-center lg:text-left">
          <div>
            <h1 className="text-5xl font-bold text-gradient mb-4">
              Vector Solutions
            </h1>
            <p className="text-xl text-slate-300 mb-6">
              Business Intelligence Command Center
            </p>
            <p className="text-slate-400 leading-relaxed">
              Plataforma multi-tenant de inteligencia empresarial con agentes AI especializados 
              para Revenue, Supply Chain, Operaciones, Logística, Finanzas y RRHH.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="glass p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-400 mb-1">6</div>
              <div className="text-sm text-slate-300">Módulos IA</div>
            </div>
            <div className="glass p-4 rounded-lg">
              <div className="text-2xl font-bold text-cyan-400 mb-1">15+</div>
              <div className="text-sm text-slate-300">Agentes Revenue</div>
            </div>
            <div className="glass p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-400 mb-1">∞</div>
              <div className="text-sm text-slate-300">Companies</div>
            </div>
            <div className="glass p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-400 mb-1">24/7</div>
              <div className="text-sm text-slate-300">Monitoring</div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <Card className="glass-dark border-blue-500/20">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-white mb-2">
              Iniciar Sesión
            </CardTitle>
            <p className="text-slate-400">
              Accede a tu centro de comando empresarial
            </p>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input pl-10"
                    placeholder="admin@vectorsolutions.com"
                    data-testid="email-input"
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input pl-10 pr-10"
                    placeholder="••••••••"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-gradient h-12 font-medium"
                data-testid="login-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Iniciando sesión...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Iniciar Sesión
                  </div>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;